import {
  CollapsedBooleanProperties,
  TimelineLabelWithRenderingParameters,
  TimelinePhasesWithRenderingParameters,
  AllItemsWithRenderingData,
  TimelineViewRenderingData,
  TimelineViewModelData,
  HasTimelineItemModel,
} from '../types';
import { getCollapsedBooleanProperties } from './calculations';
import { isTruthy } from '@ardoq/common-helpers';

type CollapseHierarchyArgs<T extends HasTimelineItemModel> =
  CollapseSelectedGroupsHierarchyArgs<T> & {
    areAllGroupsCollapsed: boolean;
    areAllGroupsExpanded: boolean;
    hasNoGroups: boolean;
  };
const collapseHierarchy = <T extends HasTimelineItemModel>({
  itemsData,
  collapsedGroupIds,
  areAllGroupsCollapsed,
  areAllGroupsExpanded,
  hasNoGroups,
}: CollapseHierarchyArgs<T>) => {
  if (hasNoGroups || areAllGroupsExpanded) {
    return itemsData;
  }
  if (areAllGroupsCollapsed) {
    return collapseAllGroupItemsHierarchy(itemsData);
  }
  return collapseSelectedGroupsHierarchy({ itemsData, collapsedGroupIds });
};

const collapseGroupItem = <T extends HasTimelineItemModel>(itemData: T) => {
  if (!itemData.itemModel.isGroup) return itemData;
  return { ...itemData, isComponentHierarchyCollapsed: true };
};

const isRootItem = <T extends HasTimelineItemModel>({ itemModel }: T) =>
  itemModel.treeDepth === 0;

const collapseAllGroupItemsHierarchy = <T extends HasTimelineItemModel>(
  itemsData: T[]
) => itemsData.filter(isRootItem).map(collapseGroupItem);

type CollapseSelectedGroupsHierarchyArgs<T extends HasTimelineItemModel> = {
  itemsData: T[];
  collapsedGroupIds: Set<string>;
};
/**
 * When an item is collapsed, skip all items with higher tree depth, and
 * reset the currentCollapsedTreeDepth when encountering a visible item.
 * A next visible item is the next item in the array that has a treedepth
 * lower than the currentCollapsedTreeDepth.
 */
const collapseSelectedGroupsHierarchy = <T extends HasTimelineItemModel>({
  itemsData,
  collapsedGroupIds,
}: CollapseSelectedGroupsHierarchyArgs<T>) => {
  let currentCollapsedTreeDepth = Infinity;
  return itemsData
    .map(itemData => {
      const {
        itemModel: { isGroup, treeDepth, rawItemIdPath },
      } = itemData;

      const isItemHiddenInCollapsedHierarchy =
        !isRootItem(itemData) && treeDepth >= currentCollapsedTreeDepth;

      if (isItemHiddenInCollapsedHierarchy) {
        return null;
      }

      const isCollapsed =
        isGroup && rawItemIdPath && collapsedGroupIds.has(rawItemIdPath);

      // This effectively collapses all the subsequent items with higher tree depth if the current
      // item is visible, is a group and is collapsed (isItemHiddenInCollapsedHierarchy), otherwise
      // we reset the currentCollapsedTreeDepth.
      currentCollapsedTreeDepth = isCollapsed ? treeDepth + 1 : Infinity;

      return isCollapsed ? collapseGroupItem(itemData) : itemData;
    })
    .filter(isTruthy);
};

type GetVisibleHierarchyArgs = {
  collapsedGroupsBooleanProperties: CollapsedBooleanProperties;
  collapsedGroupIds: Set<string>;
  allItemsWithRenderingData: AllItemsWithRenderingData;
};
const getVisibleHierarchy = ({
  allItemsWithRenderingData,
  collapsedGroupsBooleanProperties,
  collapsedGroupIds,
}: GetVisibleHierarchyArgs) => {
  const {
    areAllCollapsed,
    areAllExpanded,
    areAllRootItemsCollapsed,
    hasNoCollapsible,
  } = collapsedGroupsBooleanProperties;
  const allVisibleGroupsCollapsed = areAllCollapsed || areAllRootItemsCollapsed;
  return {
    visibleItemLabels: collapseHierarchy<TimelineLabelWithRenderingParameters>({
      itemsData: allItemsWithRenderingData.allItemLabelsWithRenderingData,
      collapsedGroupIds,
      areAllGroupsCollapsed: allVisibleGroupsCollapsed,
      areAllGroupsExpanded: areAllExpanded,
      hasNoGroups: hasNoCollapsible,
    }),
    visibleItemPhases: collapseHierarchy<TimelinePhasesWithRenderingParameters>(
      {
        itemsData: allItemsWithRenderingData.allItemPhasesWithRenderingData,
        collapsedGroupIds,
        areAllGroupsCollapsed: allVisibleGroupsCollapsed,
        areAllGroupsExpanded: areAllExpanded,
        hasNoGroups: hasNoCollapsible,
      }
    ),
  };
};

const filterOutCollapsedItems = ({
  collapsedGroupIds,
  allItemsWithRenderingData,
  collapsedGroupsBooleanProperties,
}: GetVisibleHierarchyArgs) => {
  const { visibleItemLabels, visibleItemPhases } = getVisibleHierarchy({
    collapsedGroupIds: collapsedGroupIds,
    collapsedGroupsBooleanProperties,
    allItemsWithRenderingData,
  });

  return {
    itemLabelsWithRenderingDataWithoutCollapsedHierarchy: visibleItemLabels,
    itemPhasesWithRenderingDataWithoutCollapsedHierarchy: visibleItemPhases,
  };
};

type FilterOutCollapsedHierarchyArgs = {
  timelineViewRenderingData: Pick<
    TimelineViewRenderingData,
    'collapsedGroupIds' | 'allItemsWithRenderingData'
  >;
  timelineViewModelData: TimelineViewModelData;
};
export const filterOutCollapsedHierarchy = ({
  timelineViewRenderingData: { collapsedGroupIds, allItemsWithRenderingData },
  timelineViewModelData,
}: FilterOutCollapsedHierarchyArgs) => {
  const collapsedGroupsBooleanProperties = getCollapsedBooleanProperties({
    collapsedIds: collapsedGroupIds,
    collapsibleIds: timelineViewModelData.collapsibleGroups,
  });

  const {
    itemLabelsWithRenderingDataWithoutCollapsedHierarchy,
    itemPhasesWithRenderingDataWithoutCollapsedHierarchy,
  } = filterOutCollapsedItems({
    collapsedGroupIds,
    allItemsWithRenderingData,
    collapsedGroupsBooleanProperties,
  });

  return {
    itemLabelsWithRenderingDataWithoutCollapsedHierarchy,
    itemPhasesWithRenderingDataWithoutCollapsedHierarchy,
  };
};
